import { useGridPatchedCells } from 'components/common-form/hooks/patched-fields/useGridPatchedCells';
import { useFormGlobalInfo } from 'components/common-form/hooks/use-form-global-info';
import { useFieldValidationError } from 'components/common-form/hooks/useFieldValidationError';
import { UnderlyingContext } from 'contexts/underlying/Underlying.context';
import { StrikeMetadata } from 'models/forms/fields';
import type { Underlying, UnderlyingAssetType } from 'models/underlying';
import type { UnderlyingCodeType, UnderlyingStrike } from 'models/underlying/base';
import type { CreditUnderlying } from 'models/underlying/credit';
import type { ForexUnderlying } from 'models/underlying/forex';
import { FormIdProps } from 'shared/props';

import { createUnderlyingCellHighlightResolver } from './patched-fields.helper';
import { Table } from './Table/Table';
import { Toolbar } from './Toolbar';
import { UnderlyingsValidationFeedbackMessage } from './UnderlyingsValidationFeedbackMessage';
export interface UnderlyingGridLegacyProps extends FormIdProps {
  fieldId: string;
  assetTypes: UnderlyingAssetType[];
  underlyings: readonly Underlying[];
  isReadOnly?: boolean;
  isUserInternal: boolean;
  onAllUnderlyingsRemoved?: () => void;
  onUnderlyingRemoved?: (underlyingId: string) => void;
  onUnderlyingReplaced: (underlyingId: string, newUnderlying: Underlying) => void;
  onUnderlyingStrikeChanged: (
    underlyingId: string,
    changeKey: keyof UnderlyingStrike,
    changeValue: Nullable<string> | Nullable<number>,
  ) => void;

  onUnderlyingWeightChanged: (underlyingId: string, changeValue: number) => void;
  onUnderlyingCutOffChanged: (underlyingId: string, changeValue: string) => void;
  strikeModes: string[];

  underlyingsGroupedByAssetType: Map<UnderlyingAssetType, Underlying[]>;
  defaultUnderlyingCodeType: UnderlyingCodeType;
  hasCutOff: boolean;
  strikeMetadata: StrikeMetadata;
  onCopyAll: () => void;
}

export function UnderlyingGridLegacyComponent({
  fieldId,
  assetTypes,
  formId,
  underlyings,
  isReadOnly,
  onAllUnderlyingsRemoved,
  onUnderlyingRemoved,
  onUnderlyingReplaced,
  onUnderlyingStrikeChanged,
  onUnderlyingWeightChanged,
  onUnderlyingCutOffChanged,
  strikeModes,
  underlyingsGroupedByAssetType,
  defaultUnderlyingCodeType,
  hasCutOff,
  strikeMetadata,
  onCopyAll,
}: UnderlyingGridLegacyProps) {
  const validationError = useFieldValidationError('Underlyings', 'quote.errorMessages.');
  const getGridCellHighlight = useGridPatchedCells(fieldId);
  const codeTypes: UnderlyingCodeType[] =
    defaultUnderlyingCodeType === 'Reuters'
      ? ['Reuters', 'Isin', 'Bloomberg', 'BloombergComposite', 'MicCode']
      : ['Bloomberg', 'BloombergComposite', 'Reuters', 'Isin'];

  const { isLoading } = useFormGlobalInfo(formId);
  const getCellHighlight = createUnderlyingCellHighlightResolver(getGridCellHighlight, underlyings);

  const hasClearAllButton =
    underlyingsGroupedByAssetType.size > 0 && typeof onAllUnderlyingsRemoved === 'function' && !isReadOnly;
  const hasCopyAllButton = underlyingsGroupedByAssetType.size > 0;

  return (
    <div data-e2e="selected-underlyings-list">
      <UnderlyingContext.Provider
        value={{
          codeTypes,
          isLoading: isLoading,
          isReadOnly: !!isReadOnly,
          canBeRemoved: typeof onUnderlyingRemoved === 'function',
          hasRemoveAll: hasClearAllButton,
          onUnderlyingRemoved,
          onUnderlyingReplaced,
          onUnderlyingStrikeChanged,
          onUnderlyingWeightChanged,
          onUnderlyingCutOffChanged,
          getCellHighlight,
          onRemoveAll: onAllUnderlyingsRemoved,
          strikeModes,
          hasCutOff,
        }}
      >
        {assetTypes.map(assetType => (
          <Table
            strikeMetadata={strikeMetadata}
            assetType={assetType}
            key={assetType}
            underlyings={underlyingsGroupedByAssetType.get(assetType) || []}
          />
        ))}
        {<UnderlyingsValidationFeedbackMessage validationError={validationError} />}
        <Toolbar
          hasClearAllButton={hasClearAllButton}
          hasCopyAllButton={hasCopyAllButton}
          onClearAll={onAllUnderlyingsRemoved!}
          onCopyAll={onCopyAll}
          isLoading={isLoading}
          underlyings={
            underlyingsGroupedByAssetType.get('Equity') as Exclude<Underlying, CreditUnderlying | ForexUnderlying>[]
          }
        />
      </UnderlyingContext.Provider>
    </div>
  );
}

UnderlyingGridLegacyComponent.displayName = 'UnderlyingGrid';
